import { createContext, useContext, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  type ToastContextValue,
  type ToastOptions,
  type ToastProviderProps,
  type ToastItem,
} from "./types";
import { useTheme } from "@repo/tokens";
import { resolveThemeToken } from "../../utils/theme";
import { Text } from "../../primitives/text";
import { Icon } from "../icon";

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
};

// --- Toast Component (Internal) ---
const Toast = ({
  title,
  description,
  status = "info",
  isClosable = true,
  closeIconColor = "#fff",
  render,
  onClose,
}: ToastItem & { onClose: () => void }) => {
  if (render) {
    return <>{render({ onClose })}</>;
  }

  const { theme } = useTheme();

  const colorMap: Record<string, string> = {
    info: "feedback.info",
    success: "feedback.success",
    warning: "feedback.warning",
    error: "feedback.error",
  };
  const bg = resolveThemeToken(theme, colorMap[status]) || "#333";
  const textColor = resolveThemeToken(theme, "text.inverse");

  return (
    <div
      style={{
        backgroundColor: bg,
        color: textColor,
        borderRadius: 8,
        padding: "12px 16px",
        marginBottom: 8,
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
        minWidth: 300,
        maxWidth: 400,
        animation: "fadeIn 0.3s ease",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {title && (
          <Text variant="body" style={{ fontWeight: "bold", color: textColor }}>
            {title}
          </Text>
        )}
        {isClosable ? (
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              color: closeIconColor,
              cursor: "pointer",
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Close toast"
          >
            <Icon name="closeIcon" size={16} color={closeIconColor} />
          </button>
        ) : null}
      </div>
      {description && (
        <Text variant="caption" style={{ color: "rgba(255,255,255,0.9)" }}>
          {description}
        </Text>
      )}
    </div>
  );
};

export const ToastProvider = ({
  children,
  defaultOptions,
}: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Generate simple ID
  const genId = () => Math.random().toString(36).substr(2, 9);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = genId();
      const duration = options.duration ?? defaultOptions?.duration ?? 5000;

      const newItem: ToastItem = { ...defaultOptions, ...options, id };

      setToasts((prev) => [...prev, newItem]);

      if (duration !== null) {
        setTimeout(() => {
          close(id);
        }, duration);
      }

      return id;
    },
    [defaultOptions],
  );

  const close = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const closeAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ toast, close, closeAll }}>
      {children}
      {/* Render Toasts via Portal */}
      {typeof document !== "undefined" &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: "50%",
              bottom: 24,
              transform: "translateX(-50%)",
              zIndex: 1500,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {toasts.map((t) => (
              <Toast key={t.id} {...t} onClose={() => close(t.id)} />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
};
